import { createTestRunSchema } from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { RichTextEditor } from '../../components/rich-text';
import {
  Button,
  Dialog,
  Field,
  Input,
  SelectMenu,
  SelectMenuItem,
} from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { EnvironmentSelect } from '../environments/EnvironmentSelect';
import { flattenFolders, useFolderTree } from '../folders/api';
import { LabelPicker } from '../labels/LabelPicker';
import { ChecklistPicker } from './ChecklistPicker';
import { useUsers } from '../users/api';
import { useCreateTestRun, useRunAutomationPreview } from './api';

interface RunValues {
  title: string;
  /** Empty only until a folder is chosen; a run always has one. */
  folderId: string;
  environmentId?: string | null;
  assigneeId?: string | null;
  description?: string | null;
  comment?: string | null;
}

export interface CreateRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  defaultChecklistId?: string;
  defaultTitle?: string;
  /** Folder to start from, when the list already has one selected. */
  defaultFolderId?: string | undefined;
}

const today = () => new Date().toLocaleDateString('en-GB');

/** Sentinel for the "unassigned" option: a select value is always a string. */
const NONE = 'none';

export const CreateRunDialog = ({
  open,
  onOpenChange,
  projectCode,
  defaultChecklistId,
  defaultTitle,
  defaultFolderId,
}: CreateRunDialogProps) => {
  const navigate = useNavigate();
  const { t } = useT();
  const { errorText, applyApiError } = useErrorText();
  const create = useCreateTestRun(projectCode);
  // Only load the pickers once the dialog is actually open.
  const { data: users } = useUsers({ perPage: 100 }, { enabled: open });
  const { data: folders } = useFolderTree(projectCode, 'run');
  const [checklistIds, setChecklistIds] = useState<string[]>([]);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  // On by default: a check with an autotest is what the autotest is for.
  const [runAutotests, setRunAutotests] = useState(true);
  const { can } = usePermissions();
  const canAutotests = can('automation:run');
  // The picker holds a list, so its message is shown by hand rather than by
  // the form resolver.
  const [checklistError, setChecklistError] = useState<string | undefined>(undefined);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RunValues>({
    resolver: zodResolver(createTestRunSchema.omit({ checklistIds: true, labelIds: true })),
    defaultValues: {
      title: '',
      folderId: defaultFolderId ?? '',
      environmentId: null,
      assigneeId: null,
      description: '',
      comment: '',
    },
  });

  const environmentId = watch('environmentId') ?? null;
  const { data: autotests } = useRunAutomationPreview(
    projectCode,
    checklistIds,
    environmentId,
    open && canAutotests,
  );
  const autotestsStart =
    canAutotests && runAutotests && !autotests?.blocked && (autotests?.checks ?? 0) > 0;

  useEffect(() => {
    if (!open) return;
    setChecklistIds(defaultChecklistId ? [defaultChecklistId] : []);
    setLabelIds([]);
    setRunAutotests(true);
    setChecklistError(undefined);
    reset({
      title: defaultTitle ? `${defaultTitle} — ${today()}` : '',
      folderId: defaultFolderId ?? '',
      environmentId: null,
      assigneeId: null,
      description: '',
      comment: '',
    });
  }, [open, defaultChecklistId, defaultTitle, defaultFolderId, reset]);

  const rootFolderId = folders?.[0]?.id;

  useEffect(() => {
    // A run always needs a folder, and the tree arrives after the dialog
    // opens. Start on the first one rather than on an empty field — it is the
    // only choice in a project that has not made more.
    if (!open || !rootFolderId || getValues('folderId')) return;
    setValue('folderId', rootFolderId);
  }, [open, rootFolderId, getValues, setValue]);


  const onSubmit = handleSubmit(async (values) => {
    if (checklistIds.length === 0) {
      setChecklistError(t('runs.pickAtLeastOneChecklist'));
      return;
    }
    setChecklistError(undefined);
    try {
      const run = await create.mutateAsync({
        title: values.title,
        folderId: values.folderId,
        checklistIds,
        labelIds,
        environmentId: values.environmentId ?? null,
        assigneeId: values.assigneeId ?? null,
        description: values.description?.trim() ? values.description : null,
        comment: values.comment?.trim() ? values.comment : null,
        runAutotests: autotestsStart,
      });
      toast.success(t('runs.created', { key: run.runKey }));
      onOpenChange(false);
      await navigate({
        to: '/projects/$projectCode/test-runs/$runKey',
        params: { projectCode, runKey: run.runKey },
      });
    } catch (error) {
      if (
        !applyApiError(error, setError, ['title', 'folderId', 'environmentId', 'assigneeId'])
      ) {
        toast.error(errorText(error, 'runs.createFailed'));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('runs.new')}
      description={t('runs.formHint')}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {t('runs.createShort')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t('common.title')} error={errors.title?.message} required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              autoFocus
              placeholder={t('runs.titlePlaceholder')}
              {...register('title')}
            />
          )}
        </Field>

        <Field label={t('common.folder')} error={errors.folderId?.message} required>
          {({ id, invalid, describedBy }) => (
            <Controller
              control={control}
              name="folderId"
              render={({ field }) => (
                <SelectMenu
                  // See EnvironmentSelect: a value restored after mount needs a
                  // remount for the dropdown to show its name.
                  key={field.value || 'empty'}
                  id={id}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder={t('cases.folderPlaceholder')}
                >
                  {flattenFolders(folders ?? []).map((option) => (
                    <SelectMenuItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectMenuItem>
                  ))}
                </SelectMenu>
              )}
            />
          )}
        </Field>

        <Field
          label={t('runs.fieldChecklists')}
          error={checklistError}
          hint={t('runs.fieldChecklistsHint')}
          required
        >
          {({ id }) => (
            <ChecklistPicker
              id={id}
              projectCode={projectCode}
              value={checklistIds}
              onChange={setChecklistIds}
              enabled={open}
            />
          )}
        </Field>

        <Field label={t('labels.title')}>
          {({ id }) => (
            <LabelPicker
              id={id}
              projectCode={projectCode}
              value={labelIds}
              onChange={setLabelIds}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('runs.fieldEnvironment')} error={errors.environmentId?.message}>
            {({ id, invalid }) => (
              <Controller
                control={control}
                name="environmentId"
                render={({ field }) => (
                  <EnvironmentSelect
                    id={id}
                    invalid={invalid}
                    projectCode={projectCode}
                    value={field.value ?? null}
                    onChange={field.onChange}
                    enabled={open}
                  />
                )}
              />
            )}
          </Field>
          <Field label={t('runs.fieldAssignee')} error={errors.assigneeId?.message}>
            {({ id, invalid }) => (
              <Controller
                control={control}
                name="assigneeId"
                render={({ field }) => (
                  <SelectMenu
                    id={id}
                    invalid={invalid}
                    value={field.value ?? NONE}
                    onValueChange={(value) => field.onChange(value === NONE ? null : value)}
                  >
                    <SelectMenuItem value={NONE}>{t('common.unassigned')}</SelectMenuItem>
                    {(users?.data ?? []).map((user) => (
                      <SelectMenuItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectMenuItem>
                    ))}
                  </SelectMenu>
                )}
              />
            )}
          </Field>
        </div>

        {autotests && autotests.checks > 0 ? (
          <div className="rounded-lg border border-border bg-canvas px-3 py-2.5">
            {autotests.blocked ? (
              <p className="text-[13px] text-ink-muted">
                {t('runs.autotests.create.production', { count: autotests.checks })}
              </p>
            ) : (
              <label className="flex items-start gap-2 text-[13px] text-ink">
                <input
                  type="checkbox"
                  checked={runAutotests}
                  onChange={(event) => setRunAutotests(event.target.checked)}
                  className="mt-0.5 size-4 shrink-0 rounded border-border"
                />
                <span>
                  <span className="font-medium">{t('runs.autotests.create.label')}</span>
                  <span className="mt-0.5 block text-[12px] text-ink-muted">
                    {t('runs.autotests.create.hint', {
                      count: autotests.checks,
                      projects: autotests.projects.map((project) => project.name).join(', '),
                    })}
                  </span>
                </span>
              </label>
            )}
          </div>
        ) : null}

        <Field label={t('common.description')} error={errors.description?.message}>
          {({ id, invalid }) => (
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <RichTextEditor
                  id={id}
                  invalid={invalid}
                  projectCode={projectCode}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  rows={3}
                />
              )}
            />
          )}
        </Field>

        <Field
          label={t('common.comment')}
          error={errors.comment?.message}
          hint={t('runs.fieldCommentHint')}
        >
          {({ id, invalid }) => (
            <Controller
              control={control}
              name="comment"
              render={({ field }) => (
                <RichTextEditor
                  id={id}
                  invalid={invalid}
                  projectCode={projectCode}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  rows={3}
                />
              )}
            />
          )}
        </Field>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
